import type { Logger } from "@expert/logging";

import { sendEventMetric } from "@expert/cloudwatch-metrics";
import { customCleanEnv, url } from "envalid";
import ky from "ky";

import { featureCache } from "./featureCache";
import { hashInfo } from "./hashInfo";

// Delegate at call time so spies on console.warn (e.g. in tests) still observe calls.
let logger: Pick<Logger, "warn"> = {
    warn: (...args: Parameters<Logger["warn"]>) => {
        console.warn(...args);
    },
};

const env = customCleanEnv(
    import.meta.env,
    {
        VITE_API_FEATURE_FLAG_URL: url({
            default: "https://feature.remoteexpert.anywhereexpert.us",
        }),
        VITE_API_K8S_FEATURE_FLAG_URL: url({
            default: "https://feature.remoteexpert.npr.aws.asurion.net/api",
        }),
    },
    (
        inputEnv,
    ): {
        apiFeatureFlagUrl: string;
        apiK8sFeatureFlagUrl: string;
    } => ({
        apiFeatureFlagUrl: inputEnv.VITE_API_FEATURE_FLAG_URL,
        apiK8sFeatureFlagUrl: inputEnv.VITE_API_K8S_FEATURE_FLAG_URL,
    }),
);

function featureValuesMatch<T>(v1Value: T | undefined, v2Value: T | undefined): boolean {
    return JSON.stringify(v1Value) === JSON.stringify(v2Value);
}

async function compareWithV2<T>(key: string, context: Record<string, unknown>, v1Value: T | undefined): Promise<void> {
    try {
        const v2Value = await getFeatureV2<T>(key, context);

        if (!featureValuesMatch(v1Value, v2Value)) {
            logger.warn({ featureKey: key, v1Value, v2Value }, "Feature flag V1 and V2 responses do not match");
            void sendEventMetric("featureFlag.v1v2Mismatch");
        }
    } catch (error) {
        logger.warn({ error }, "error comparing feature flag V1 and V2");
    }
}

export function setFeatureLogger(newLogger: Logger) {
    logger = newLogger;
}

/*
 * key - feature key to fetch
 * context - context about the requester to determine the value of the key
 * cacheTtl - ttl of in-memory cache to reduce load on the feature toggle server (in seconds)
 */
export const getFeature = async <T>(
    key: string,
    context: Record<string, unknown>,
    cacheTtl = 60,
): Promise<T | undefined> => {
    try {
        const hashedInfo = await hashInfo(key + JSON.stringify(context));

        if (cacheTtl > 0 && featureCache.has(hashedInfo)) {
            return featureCache.get<T>(hashedInfo);
        }

        const response = await fetch(`${env.apiFeatureFlagUrl}/v1/feature/${key}/fetch`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ context: { ...context } }),
        });

        const value = ((await response.json()) as { value: T }).value;
        featureCache.set(hashedInfo, value, cacheTtl);
        void compareWithV2(key, context, value);
        return value;
    } catch (error) {
        logger.warn({ error }, "error fetching the feature key");
        return undefined;
    }
};

const getFeatureV2 = async <T>(
    key: string,
    context: Record<string, unknown>,
    // cacheTtl = 60,
): Promise<T | undefined> => {
    try {
        // TODO: Uncomment this once we verify that V2 feature flag returns the same value as V1
        // const hashedInfo = await hashInfo(key + JSON.stringify(context));

        // if (cacheTtl > 0 && featureCache.has(hashedInfo)) {
        //     return featureCache.get<T>(hashedInfo);
        // }

        const { value } = await ky
            .post(`${env.apiK8sFeatureFlagUrl}/v1/feature/${key}/fetch`, {
                json: { context: { ...context } },
                retry: { limit: 3, methods: ["POST"], retryOnTimeout: true, jitter: true, delay: () => 0 },
            })
            .json<{ value: T }>();

        // TODO: Uncomment this once we verify that V2 feature flag returns the same value as V1
        // featureCache.set(hashedInfo, value, cacheTtl);
        return value;
    } catch (error) {
        logger.warn({ error }, "error fetching the feature key from V2");
        return undefined;
    }
};
